import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@shape-ds/components';
import { AppContext } from 'appContext';
import {
  useCommentMutation,
  useInvalidateRecordsQueries,
  useOptimisticUpdateQuery,
} from 'queries/events/records';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGlobalNotification } from 'hooks';
import { KeyTypesUpload, RecordCategory, RecordCategoryType } from 'queries/events/records/types';
import './style.scss';

import { useDownloadFile, useFileDeleteMutation, useFilesListQuery } from 'queries';
import { UploadButton } from 'ui/UploadButton/UploadButton';
import { downloadBase64File } from 'utils';

import { handleUploadFile } from '../utils';

interface RecordsFilesProps {
  eventId: string;
}

export const RecordsFiles = ({ eventId }: RecordsFilesProps) => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const { displaySnackbar } = useGlobalNotification();

  const optimisticUpdate = useOptimisticUpdateQuery(['RECORDS', eventId]);
  const refetchRecords = useInvalidateRecordsQueries(eventId);

  const newCommentMutation = useCommentMutation();

  const [fileToDownload, setFileToDownload] = useState<string | null>(null);
  const deleteMutation = useFileDeleteMutation(refetchRecords);
  const fileKeys = { keyName: eventId, keyType: KeyTypesUpload.RECORDS };

  const { data: filesData, refetch: refetchListFiles } = useFilesListQuery({
    ...fileKeys,
    showAuditLog: true,
  });
  const { data: dataToDownload, refetch: refetchDownload } = useDownloadFile({
    ...fileKeys,
    fileName: fileToDownload || '',
  });

  useEffect(() => {
    if (fileToDownload && dataToDownload) {
      refetchDownload();
      downloadBase64File(dataToDownload, fileToDownload);

      setFileToDownload(null);
    }
  }, [fileToDownload, dataToDownload]);

  const handleSubmit = (text: string, typeUpload: RecordCategoryType) => {
    const revertUpdate = optimisticUpdate({
      user_email: state.auth.user?.email || '',
      comment: text,
      category: typeUpload,
    });

    newCommentMutation.mutate(
      {
        eventId,
        text,
        category: typeUpload,
      },
      {
        onSuccess: () => {
          displaySnackbar({
            message: t('messages:success.message'),
            type: 'success',
          });
          refetchListFiles();
        },
        onError: () => {
          displaySnackbar({
            message: t('messages:fetch_error.message'),
            type: 'error',
          });
          revertUpdate();
        },
        onSettled: () => {
          refetchRecords();
        },
      },
    );
  };

  return (
    <div className="records-actions">
      <UploadButton
        alarmId={eventId}
        handleUploadFile={handleUploadFile}
        onSuccessUpdateFile={(message: string) => {
          handleSubmit(message, RecordCategory.ATTACHED_FILE);
        }}
        isDeletingFile={deleteMutation.isLoading}
      />
      <div className="info-button">
        <Tooltip
          style={{ height: '36px' }}
          title="Allowed file formats: XLS, TXT, PDF, DOC, JPG and PNG with size up to 2Mb"
        >
          <InfoOutlinedIcon />
        </Tooltip>
      </div>
      {filesData?.files.map((fileName) => (
        <div key={fileName} className="attached-button">
          <div
            className="download-button-icon"
            key={fileName}
            id={`downloadFile-${fileToDownload}`}
            onClick={() => {
              setFileToDownload(fileName);
            }}
          >
            {fileName}
          </div>
          <div
            className="detached-button-icon"
            onClick={(e) => {
              e.stopPropagation();
              deleteMutation.mutate(
                { ...fileKeys, fileName },
                {
                  onSuccess: () => {
                    handleSubmit(fileName.trim(), RecordCategory.DETACHED_FILE);
                  },
                },
              );
            }}
          >
            <FontAwesomeIcon icon={faTrashCan} data-testid="delete-file" />
          </div>
        </div>
      ))}
    </div>
  );
};
